/**
 * copilot.ts — browser-direct LLM client for the Lua IDE (Anthropic or OpenAI).
 *
 * Self-hosted rule: the key is the USER's, entered in the IDE's AI settings and
 * stored in localStorage — no backend of ours ever sees it. Anthropic supports
 * browser calls natively via the `anthropic-dangerous-direct-browser-access`
 * header; OpenAI generally allows CORS on /v1/chat/completions. If a direct
 * call is CORS-blocked, we retry through the Vite dev proxy (`/ai/<provider>`),
 * which start.sh serves in dev.
 *
 * Three call shapes:
 *   chatStream()     — streaming text chat (no tools) → the chat panel
 *   chatOnce()       — non-streaming, WITH tools → one agent round
 *   runAgent()       — chatOnce loop executing tools (read scripts, lint,
 *                      stage edits) until the model stops or maxRounds
 *   completeInline() — tiny fill-in-the-middle call → ghost-text completion
 */

export type Provider = 'anthropic' | 'openai';

export interface CopilotSettings {
  provider: Provider;
  apiKey: string;
  model: string;
}

export interface ChatMsg { role: 'user' | 'assistant'; content: string }

export interface ToolDef {
  name: string;
  description: string;
  /** JSON-schema properties (all string-typed keeps both providers happy). */
  params: Record<string, { type: string; description: string }>;
  required?: string[];
  run(args: Record<string, unknown>): Promise<string> | string;
}

export interface ToolCall { id: string; name: string; args: Record<string, unknown> }

const LS_KEY = 'atlas:copilot';
export const DEFAULT_MODEL: Record<Provider, string> = {
  anthropic: 'claude-sonnet-5',
  openai: 'gpt-5',
};

export function loadSettings(): CopilotSettings {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (raw) return { provider: 'anthropic', apiKey: '', model: DEFAULT_MODEL.anthropic, ...JSON.parse(raw) as Partial<CopilotSettings> };
  } catch { /* fresh */ }
  return { provider: 'anthropic', apiKey: '', model: DEFAULT_MODEL.anthropic };
}

export function saveSettings(s: CopilotSettings): void {
  try { localStorage.setItem(LS_KEY, JSON.stringify(s)); } catch { /* private mode */ }
}

// ── transport (direct → dev-proxy fallback on CORS/network refusal) ───────────
const DIRECT: Record<Provider, string> = {
  anthropic: 'https://api.anthropic.com',
  openai: 'https://api.openai.com',
};
const proxied: Partial<Record<Provider, boolean>> = {};

async function post(provider: Provider, path: string, key: string, body: unknown, signal?: AbortSignal): Promise<Response> {
  const headers: Record<string, string> = { 'content-type': 'application/json' };
  if (provider === 'anthropic') {
    headers['x-api-key'] = key;
    headers['anthropic-version'] = '2023-06-01';
    headers['anthropic-dangerous-direct-browser-access'] = 'true';
  } else {
    headers['authorization'] = `Bearer ${key}`;
  }
  const attempt = (base: string): Promise<Response> =>
    fetch(`${base}${path}`, { method: 'POST', headers, body: JSON.stringify(body), signal });
  if (!proxied[provider]) {
    try {
      return await attempt(DIRECT[provider]);
    } catch (e) {
      if (signal?.aborted) throw e;
      proxied[provider] = true; // CORS/network — fall through to the dev proxy
    }
  }
  return attempt(`/ai/${provider}`);
}

async function readError(res: Response): Promise<string> {
  try {
    const j = await res.json() as { error?: { message?: string } };
    return j.error?.message ?? `HTTP ${res.status}`;
  } catch { return `HTTP ${res.status}`; }
}

// ── streaming chat (no tools) ─────────────────────────────────────────────────
export async function chatStream(
  s: CopilotSettings,
  system: string,
  messages: ChatMsg[],
  onText: (delta: string) => void,
  signal?: AbortSignal,
): Promise<string> {
  let full = '';
  const emit = (d: string): void => { if (d) { full += d; onText(d); } };
  if (s.provider === 'anthropic') {
    const res = await post('anthropic', '/v1/messages', s.apiKey, {
      model: s.model, max_tokens: 4000, system, stream: true,
      messages: messages.map((m) => ({ role: m.role, content: m.content })),
    }, signal);
    if (!res.ok || !res.body) throw new Error(await readError(res));
    await readSse(res.body, (json) => {
      const ev = json as { type?: string; delta?: { type?: string; text?: string } };
      if (ev.type === 'content_block_delta' && ev.delta?.type === 'text_delta') emit(ev.delta.text ?? '');
    }, signal);
  } else {
    const res = await post('openai', '/v1/chat/completions', s.apiKey, {
      model: s.model, stream: true,
      messages: [{ role: 'system', content: system }, ...messages],
    }, signal);
    if (!res.ok || !res.body) throw new Error(await readError(res));
    await readSse(res.body, (json) => {
      const ev = json as { choices?: Array<{ delta?: { content?: string } }> };
      emit(ev.choices?.[0]?.delta?.content ?? '');
    }, signal);
  }
  return full;
}

/** Parse an SSE stream, calling onJson for each `data: {...}` event ([DONE] skipped). */
async function readSse(body: ReadableStream<Uint8Array>, onJson: (j: unknown) => void, signal?: AbortSignal): Promise<void> {
  const reader = body.getReader();
  const dec = new TextDecoder();
  let buf = '';
  for (;;) {
    if (signal?.aborted) { void reader.cancel(); return; }
    const { done, value } = await reader.read();
    if (done) return;
    buf += dec.decode(value, { stream: true });
    let nl: number;
    while ((nl = buf.indexOf('\n')) !== -1) {
      const lineRaw = buf.slice(0, nl);
      buf = buf.slice(nl + 1);
      const line = lineRaw.trim();
      if (!line.startsWith('data:')) continue;
      const data = line.slice(5).trim();
      if (!data || data === '[DONE]') continue;
      try { onJson(JSON.parse(data)); } catch { /* partial frame — ignore */ }
    }
  }
}

// ── one agent round (tools, non-streaming — simple and provider-uniform) ──────
export interface AgentTurn {
  text: string;
  toolCalls: ToolCall[];
  /** provider-native assistant message to append back into history */
  raw: unknown;
}

type WireMsg = { role: string; content: unknown; tool_call_id?: string; tool_calls?: unknown };

export async function chatOnce(
  s: CopilotSettings,
  system: string,
  wire: WireMsg[],
  tools: ToolDef[],
  signal?: AbortSignal,
): Promise<AgentTurn> {
  if (s.provider === 'anthropic') {
    const res = await post('anthropic', '/v1/messages', s.apiKey, {
      model: s.model, max_tokens: 4000, system,
      messages: wire,
      tools: tools.map((t) => ({
        name: t.name, description: t.description,
        input_schema: { type: 'object', properties: t.params, required: t.required ?? [] },
      })),
    }, signal);
    if (!res.ok) throw new Error(await readError(res));
    const j = await res.json() as { content: Array<{ type: string; text?: string; id?: string; name?: string; input?: Record<string, unknown> }> };
    const text = j.content.filter((c) => c.type === 'text').map((c) => c.text).join('');
    const toolCalls = j.content.filter((c) => c.type === 'tool_use').map((c) => ({ id: c.id ?? '', name: c.name ?? '', args: c.input ?? {} }));
    return { text, toolCalls, raw: { role: 'assistant', content: j.content } };
  }
  const res = await post('openai', '/v1/chat/completions', s.apiKey, {
    model: s.model,
    messages: [{ role: 'system', content: system }, ...wire],
    tools: tools.map((t) => ({
      type: 'function',
      function: { name: t.name, description: t.description, parameters: { type: 'object', properties: t.params, required: t.required ?? [] } },
    })),
  }, signal);
  if (!res.ok) throw new Error(await readError(res));
  const j = await res.json() as { choices: Array<{ message: { content?: string; tool_calls?: Array<{ id: string; function: { name: string; arguments: string } }> } }> };
  const msg = j.choices[0]?.message;
  const toolCalls = (msg?.tool_calls ?? []).map((c) => {
    let args: Record<string, unknown> = {};
    try { args = JSON.parse(c.function.arguments) as Record<string, unknown>; } catch { /* leave empty */ }
    return { id: c.id, name: c.function.name, args };
  });
  return { text: msg?.content ?? '', toolCalls, raw: { role: 'assistant', content: msg?.content ?? null, tool_calls: msg?.tool_calls } };
}

/** Append a turn's tool results in the provider's wire shape. */
function toolResults(provider: Provider, turn: AgentTurn, results: Array<{ id: string; out: string }>): WireMsg[] {
  if (provider === 'anthropic') {
    return [{
      role: 'user',
      content: results.map((r) => ({ type: 'tool_result', tool_use_id: r.id, content: r.out.slice(0, 24_000) })),
    }];
  }
  return results.map((r) => ({ role: 'tool', tool_call_id: r.id, content: r.out.slice(0, 24_000) }));
}

export interface AgentEvents {
  onText(text: string): void;                       // assistant prose per round
  onTool(name: string, args: Record<string, unknown>): void;  // before each execution
  onToolResult(name: string, out: string): void;    // after each execution
}

/** The agent loop: think → call tools → observe → repeat (bounded). */
export async function runAgent(
  s: CopilotSettings,
  system: string,
  userPrompt: string,
  tools: ToolDef[],
  ev: AgentEvents,
  maxRounds = 8,
  signal?: AbortSignal,
): Promise<string> {
  const wire: WireMsg[] = [{ role: 'user', content: userPrompt }];
  const byName = new Map(tools.map((t) => [t.name, t]));
  let lastText = '';
  for (let round = 0; round < maxRounds; round++) {
    const turn = await chatOnce(s, system, wire, tools, signal);
    if (turn.text.trim()) { lastText = turn.text; ev.onText(turn.text); }
    if (turn.toolCalls.length === 0) return lastText;
    wire.push(turn.raw as WireMsg);
    const results: Array<{ id: string; out: string }> = [];
    for (const call of turn.toolCalls) {
      ev.onTool(call.name, call.args);
      let out: string;
      try {
        const tool = byName.get(call.name);
        out = tool ? String(await tool.run(call.args)) : `unknown tool: ${call.name}`;
      } catch (e) {
        out = `tool error: ${(e as Error).message}`;
      }
      ev.onToolResult(call.name, out);
      results.push({ id: call.id, out });
    }
    wire.push(...toolResults(s.provider, turn, results));
  }
  return lastText || '(agent hit the round limit)';
}

// ── inline ghost completion (fill-in-the-middle) ──────────────────────────────
const FIM_SYSTEM =
  'You complete Lua code for a game scripting IDE. Given the text before and after the cursor, ' +
  'output ONLY the code to insert at the cursor — no backticks, no commentary, no repetition of ' +
  'surrounding text. Prefer 1-3 lines. If nothing useful fits, output nothing.';

export async function completeInline(
  s: CopilotSettings,
  apiDocs: string,
  prefix: string,
  suffix: string,
  signal?: AbortSignal,
): Promise<string> {
  const user = `API available:\n${apiDocs}\n\n<before-cursor>\n${prefix.slice(-2000)}\n</before-cursor>\n<after-cursor>\n${suffix.slice(0, 600)}\n</after-cursor>\nInsert at cursor:`;
  if (s.provider === 'anthropic') {
    const res = await post('anthropic', '/v1/messages', s.apiKey, {
      model: s.model, max_tokens: 120, system: FIM_SYSTEM,
      messages: [{ role: 'user', content: user }],
    }, signal);
    if (!res.ok) throw new Error(await readError(res));
    const j = await res.json() as { content: Array<{ type: string; text?: string }> };
    return j.content.filter((c) => c.type === 'text').map((c) => c.text).join('').replace(/^```\w*\n?|```$/g, '');
  }
  const res = await post('openai', '/v1/chat/completions', s.apiKey, {
    model: s.model, max_tokens: 120,
    messages: [{ role: 'system', content: FIM_SYSTEM }, { role: 'user', content: user }],
  }, signal);
  if (!res.ok) throw new Error(await readError(res));
  const j = await res.json() as { choices: Array<{ message: { content?: string } }> };
  return (j.choices[0]?.message.content ?? '').replace(/^```\w*\n?|```$/g, '');
}
